import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, getActiveClubId, isAdmin } from "../lib/api";
import { Trophy, Flame, Target, Award, Activity, Swords, Sparkles } from "lucide-react";

export default function Dashboard() {
  const admin = isAdmin();
  const [season, setSeason] = useState(null);
  const [standings, setStandings] = useState([]);
  const [leaders, setLeaders] = useState(null);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [extra, setExtra] = useState({ h2h_highlight: null, record_of_week: null, longest_win_streak: null, current_win_streak: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, ps] = await Promise.all([api.activeSeason(), api.listPlayers()]);
        setSeason(s); setPlayers(ps);
        if (s) {
          const [tab, lb, ms, ex] = await Promise.all([
            api.tableSeason(s.id), api.leaderboardsSeason(s.id),
            api.listMatches({ season_id: s.id }), api.dashboardExtra(s.id),
          ]);
          setStandings(tab); setLeaders(lb); setMatches(ms); setExtra(ex);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const pmap = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const artworkByClub = {
    "dc-atenser-allee": { background: "/dashboard/dcaa-dashboard.png", logo: "/dashboard/dcaa-logo.jpg" },
    "dc-pfeile-statt-worte": { background: "/dashboard/psw-dashboard.png", logo: "/dashboard/psw-logo.png" },
  };
  const selectedClubArtwork = artworkByClub[getActiveClubId()] || null;

  if (loading) return <div className="font-display text-2xl" data-testid="dashboard-loading">LADE DATEN...</div>;

  if (!season) {
    return (
      <section className="relative overflow-hidden dc-border p-10 md:p-14 text-center" style={{ background: "#101010" }} data-testid="no-season">
        <div className="absolute inset-0 pointer-events-none">
          <img src={selectedClubArtwork?.background || "https://images.pexels.com/photos/18719128/pexels-photo-18719128.jpeg"} alt="" className="w-full h-full object-cover opacity-75" />
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.48)" }} />
        </div>
        <div className="relative flex flex-col items-center">
          {selectedClubArtwork?.logo && <img src={selectedClubArtwork.logo} alt="Club Logo" className="w-28 h-28 object-contain dc-border p-2 mb-6" style={{ background: "rgba(0,0,0,0.75)" }} />}
          <div className="font-display text-5xl mb-2">KEINE AKTIVE SAISON</div>
          <p style={{ color: "var(--dc-muted)" }}>Erstelle im Admin-Bereich eine neue Saison, um zu starten.</p>
          <Link to="/admin" className="dc-btn mt-6 inline-flex" data-testid="dashboard-to-admin">Zum Admin</Link>
        </div>
      </section>
    );
  }

  const config = season.dashboard_config || {};
  const defaultWidgets = [
    { id: "hero", visible: true }, { id: "leaders", visible: true }, { id: "streaks", visible: true },
    { id: "record_week", visible: true }, { id: "h2h_highlight", visible: true },
    { id: "standings", visible: true }, { id: "upcoming", visible: true },
  ];
  const storedWidgets = config.widgets || [];
  const storedIds = new Set(storedWidgets.map((w) => w.id));
  const mergedWidgets = [
    ...storedWidgets,
    ...defaultWidgets.filter((w) => !storedIds.has(w.id)),
  ];
  const played = matches.filter((m) => m.played).length;
  const upcoming = matches.filter((m) => !m.played).slice(0, 5);
  const topLeader = leaders?.average?.[0];
  const s180Leader = leaders?.s180?.[0];
  const hfLeader = leaders?.high_finish?.[0];
  const title = config.title || season.name;
  const clubArtwork = artworkByClub[season.club_id] || selectedClubArtwork;
  const heroLogo = clubArtwork?.logo || season.custom_logo_url;

  const recordLabel = { high_finish: "Highest Finish", s180: "180er", average: "Match-Average" };

  const render = {
    hero: (
      <section key="hero" className="relative overflow-hidden dc-border p-8 md:p-12" style={{ background: "#101010" }} data-testid="widget-hero-render">
        <div className="absolute inset-0 pointer-events-none">
          <img src={clubArtwork?.background || "https://images.pexels.com/photos/10792812/pexels-photo-10792812.jpeg"} alt="" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(8,8,8,0.20), rgba(8,8,8,0.02) 70%, rgba(8,8,8,0.18))" }} />
        </div>
        <div className="relative flex items-center gap-6 flex-wrap">
          {heroLogo && (
            <img src={heroLogo} alt="Club Logo" className="w-28 h-28 md:w-32 md:h-32 object-contain dc-border p-2" style={{ background: "rgba(0,0,0,0.72)" }} data-testid="dashboard-club-logo" />
          )}
          <div>
            <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>Aktive Saison</div>
            <h1 className="font-display text-5xl md:text-7xl mt-2" data-testid="active-season-name">{title}</h1>
            <div className="mt-4 flex flex-wrap gap-6 font-mono-data text-sm" style={{ color: "var(--dc-muted)" }}>
              <span><span className="text-white text-lg">{(season.member_ids || season.player_ids || []).length}</span> Stammspieler</span>
              <span><span className="text-white text-lg">{season.num_matchdays || "—"}</span> Spieltage</span>
              <span><span className="text-white text-lg">{matches.length}</span> Partien</span>
              <span><span className="text-white text-lg">{played}</span> gespielt</span>
            </div>
          </div>
        </div>
      </section>
    ),
    leaders: (
      <section key="leaders" className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="widget-leaders-render">
        <LeaderTile icon={Activity} label="Bester Average" value={topLeader?.average || 0} name={topLeader?.name} color="var(--dc-secondary)" testid="leader-avg" />
        <LeaderTile icon={Flame} label="Meiste 180er" value={s180Leader?.s180 || 0} name={s180Leader?.name} color="var(--dc-primary)" testid="leader-180" />
        <LeaderTile icon={Award} label="Highest Finish" value={hfLeader?.high_finish || 0} name={hfLeader?.name} color="var(--dc-chalk)" testid="leader-hf" />
      </section>
    ),
    streaks: (
      <section key="streaks" className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="widget-streaks-render">
        <LeaderTile icon={Trophy} label="Längste Siegesserie" value={extra.longest_win_streak?.value || 0} name={extra.longest_win_streak?.player?.name} note={extra.longest_win_streak?.period} color="var(--dc-chalk)" testid="leader-longest-streak" />
        <LeaderTile icon={Flame} label="Aktuelle Siegesserie" value={extra.current_win_streak?.value || 0} name={extra.current_win_streak?.player?.name} note={extra.current_win_streak?.period} color="var(--dc-primary)" testid="leader-current-streak" />
      </section>
    ),
    record_week: extra.record_of_week ? (
      <section key="record_week" className="dc-border p-6 flex items-center justify-between gap-6" style={{ background: "linear-gradient(90deg, rgba(245,209,59,0.10), transparent)" }} data-testid="widget-record-week-render">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "var(--dc-chalk)", color: "#000" }}>
            <Sparkles size={26} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>Rekord der Woche</div>
            <div className="font-display text-3xl">{extra.record_of_week.player.name}</div>
            <div className="text-sm" style={{ color: "var(--dc-muted)" }}>
              {recordLabel[extra.record_of_week.type] || extra.record_of_week.type}
            </div>
          </div>
        </div>
        <div className="font-display text-6xl font-mono-data" style={{ color: "var(--dc-chalk)" }}>{extra.record_of_week.value}</div>
      </section>
    ) : null,
    h2h_highlight: extra.h2h_highlight ? (
      <section key="h2h_highlight" className="dc-border p-6" data-testid="widget-h2h-render">
        <div className="flex items-center gap-2 mb-4">
          <Swords size={18} style={{ color: "var(--dc-primary)" }} />
          <h2 className="font-display text-2xl">Head-to-Head Highlight</h2>
        </div>
        <div className="grid grid-cols-3 items-center gap-4">
          <div className="text-center md:text-right">
            <div className="font-display text-3xl">{extra.h2h_highlight.player1.name}</div>
          </div>
          <div className="text-center">
            <div className="font-mono-data font-display text-5xl flex items-center justify-center gap-3">
              <span style={{ color: extra.h2h_highlight.p1_wins > extra.h2h_highlight.p2_wins ? "var(--dc-secondary)" : "var(--dc-text)" }}>{extra.h2h_highlight.p1_wins}</span>
              <span style={{ color: "var(--dc-muted)" }}>:</span>
              <span style={{ color: extra.h2h_highlight.p2_wins > extra.h2h_highlight.p1_wins ? "var(--dc-secondary)" : "var(--dc-text)" }}>{extra.h2h_highlight.p2_wins}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: "var(--dc-muted)" }}>{extra.h2h_highlight.matches_played} Duelle</div>
          </div>
          <div className="text-center md:text-left">
            <div className="font-display text-3xl">{extra.h2h_highlight.player2.name}</div>
          </div>
        </div>
      </section>
    ) : null,
    standings: (
      <section key="standings" className="dc-border p-6" data-testid="widget-standings-render">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl flex items-center gap-2"><Trophy size={20} /> Saisontabelle</h2>
          <Link to="/tabelle" className="text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }} data-testid="dashboard-view-full-table">Vollständige Tabelle →</Link>
        </div>
        <table className="w-full font-mono-data text-sm">
          <thead><tr style={{ color: "var(--dc-muted)" }} className="text-xs uppercase">
            <th className="text-left py-2 w-8">#</th><th className="text-left">Spieler</th>
            <th className="text-right">Sp</th><th className="text-right">S</th><th className="text-right">N</th>
            <th className="text-right">Leg±</th><th className="text-right">Pkt</th>
          </tr></thead>
          <tbody>
            {standings.slice(0, 5).map((r, i) => (
              <tr key={r.player_id} className="row-in border-t dc-border" style={{ animationDelay: `${i * 40}ms` }} data-testid={`standings-row-${r.player_id}`}>
                <td className="py-3"><span className="inline-block w-6 text-center" style={{ background: i === 0 ? "var(--dc-primary)" : "transparent", color: i === 0 ? "white" : "inherit" }}>{r.rank}</span></td>
                <td style={{ fontFamily: "Manrope" }}><Link to={`/spieler/${r.player_id}`} className="hover:underline">{r.name}</Link></td>
                <td className="text-right">{r.played}</td>
                <td className="text-right" style={{ color: "var(--dc-secondary)" }}>{r.wins}</td>
                <td className="text-right" style={{ color: "var(--dc-primary)" }}>{r.losses}</td>
                <td className="text-right">{r.leg_diff > 0 ? "+" : ""}{r.leg_diff}</td>
                <td className="text-right font-bold text-white">{r.points}</td>
              </tr>
            ))}
            {standings.length === 0 && <tr><td colSpan={7} className="py-6 text-center" style={{ color: "var(--dc-muted)" }}>Noch keine gespielten Partien.</td></tr>}
          </tbody>
        </table>
      </section>
    ),
    upcoming: (
      <section key="upcoming" className="dc-border p-6" data-testid="widget-upcoming-render">
        <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><Target size={20} /> Nächste Partien</h2>
        <div className="space-y-3">
          {upcoming.length === 0 && <p style={{ color: "var(--dc-muted)" }}>Keine offenen Partien.</p>}
          {upcoming.map((m) => (
            <div key={m.id} className="dc-border p-3 flex items-center justify-between text-sm hover:bg-[color:var(--dc-surface-hover)]" data-testid={`upcoming-${m.id}`}>
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--dc-chalk)" }}>Spieltag {m.matchday_number || "?"}</div>
                <div className="mt-1">
                  <span>{pmap[m.player1_id]?.name || "?"}</span>
                  <span className="mx-2" style={{ color: "var(--dc-muted)" }}>vs</span>
                  <span>{pmap[m.player2_id]?.name || "?"}</span>
                </div>
              </div>
              {admin && <Link to={`/erfassen?match=${m.id}`} className="dc-btn dc-btn-ghost text-xs" data-testid={`upcoming-enter-${m.id}`}>Eintragen</Link>}
            </div>
          ))}
        </div>
      </section>
    ),
  };

  return (
    <div className="space-y-8" data-testid="dashboard">
      {mergedWidgets.filter((w) => w.visible).map((w) => render[w.id] || null)}
    </div>
  );
}

function LeaderTile({ icon: Icon, label, value, name, note, color, testid }) {
  return (
    <div className="dc-border p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}>{label}</span>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="font-display text-6xl font-mono-data" style={{ color }}>{value || "—"}</div>
      <div className="mt-2 text-sm" style={{ color: "var(--dc-text)" }}>{name || "—"}</div>
      {note && <div className="mt-1 text-xs" style={{ color: "var(--dc-muted)" }}>{note}</div>}
    </div>
  );
}
